import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs';

import { DEBOUNCE_TIMEOUT } from '@shared-constants/ui-constants';
import { getOdataString } from '@shared-helpers/date-helpers';
import { IHttpResponseEmployee, IHttpResponseEquipment } from '@shared-interfaces/api';

import { environment } from '../../../../environments/environment';
import { DatahubService } from '../../../services/datahub.service';
import { ModalService } from '../../../services/modal.service';

@Component({
  selector: 'app-modal-content-entity-number-lookup',
  templateUrl: './modal-content-entity-number-lookup.component.html',
  standalone: false,
})
export class ModalContentEntityNumberLookupComponent implements OnInit {
  public entityNumCtrl = new FormControl<string>('');
  public debouncedValue = '';

  private entity: IHttpResponseEquipment | IHttpResponseEmployee | null = null;

  @Input() public entityType: 'employee' | 'equipment' = 'employee'; //Set a default so we always have a value
  @Output() public readonly closed = new EventEmitter<IHttpResponseEquipment | IHttpResponseEmployee | null>();

  constructor(
    private readonly datahub: DatahubService,
    private readonly modalService: ModalService,
  ) {}

  public ngOnInit(): void {
    this.entityNumCtrl.markAsTouched(); // Allows UI validation to trigger immediately

    this.entityNumCtrl.valueChanges.pipe(debounceTime(DEBOUNCE_TIMEOUT)).subscribe({
      next: (num: string) => {
        this.handleValueChange(num);
      },
      error: (err) => {
        console.error('Error in entityNumCtrl valueChanges subscription:', err);
        this.datahub.messageService.error('An error occurred while processing the value change.');
      },
    });
  }

  public close(): void {
    this.closed.emit(this.entity);
  }

  public get entityResultDisplay(): string {
    if (this.entity) {
      if (this.entityType === 'employee') {
        const employee = this.entity as IHttpResponseEmployee;
        return `${employee.FirstName} ${employee.LastName}`;
      } else {
        const equipment = this.entity as IHttpResponseEquipment;
        return equipment.Description;
      }
    }
    return '';
  }

  public easyTicket() {
    const humanDate = this.datahub.weDate.getHumanReadableDateString();
    const formattedEntityType = this.capitalizeFirstLetter(this.entityType);
    let messageSeed = `${formattedEntityType} #${this.debouncedValue} is invalid on ${humanDate}.\nPlease investigate this issue and contact me when it is resolved.`;

    if (this.entityType === 'equipment' && this.entity) {
      messageSeed = `Equipment #${this.debouncedValue} is missing cost for this year in KP26. \n\nIf the equipment is in Units for Sale or Work Order - please reach out to ${this.datahub.user.approvingName} to confirm next steps.\n\nThe accounting department (FA@Davey.com) will need to add the pricing for the current year.`;
    }

    this.modalService.changeCurrentlyOpenModalComponent('need-help', {
      messageSeed,
      emailAddressOverride: this.entityType === 'equipment' ? environment.equipmentSupportEmailAddress : undefined, //Equipment issues are sent to a different address, otherwise use the default from the help component
      subjectOverride: `Davey Time ${formattedEntityType} Issue`,
    });
  }

  private capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private handleValueChange(value: string): void {
    this.debouncedValue = value;
    this.entity = null;

    if (this.entityNumCtrl.valid && this.entityNumCtrl.value.length > 0) {
      const dateStr = `datetime'${getOdataString(this.datahub.weDate.getWeDate())}'`;
      this.entityNumCtrl.markAsPending();
      if (this.entityType === 'employee') {
        this.findEmployee(value);
      } else if (this.entityType === 'equipment') {
        this.findEquipment(value, dateStr);
      }
    }
  }

  private findEmployee(EmpId: string): void {
    this.datahub.apiService.getOneEmployee$(this.datahub.weDate.getSapDateString(), EmpId, false).subscribe({
      next: (foundEmployee) => {
        if (foundEmployee) {
          this.entity = foundEmployee;
          this.entityNumCtrl.setErrors(null);
        } else {
          this.entityNumCtrl.setErrors({ notEntity: true });
        }
      },
      error: (err) => {
        console.error('Error while finding employee:', err);
        this.datahub.messageService.error('An error occurred while retrieving the employee.');
        this.entityNumCtrl.setErrors({ serverError: true });
      },
    });
  }

  private findEquipment(EquipmentId: string, ValidTo: string): void {
    this.datahub.apiService
      .getOneEquipment$({
        EquipmentId,
        ValidTo,
      })
      .subscribe({
        next: (foundEquipment) => {
          this.entity = foundEquipment;

          if (foundEquipment) {
            // Only showing error in modal when equipment is unavailable for the WHOLE week
            if (foundEquipment.InvalidDates === '0,1,2,3,4,5,6') {
              this.entityNumCtrl.setErrors({ unavailable: true });
            } else {
              this.entityNumCtrl.setErrors(null);
            }
          } else {
            this.entityNumCtrl.setErrors({ notEntity: true });
          }
        },
        error: (err) => {
          this.datahub.messageService.error('An error occurred while retrieving the equipment.', err);
          this.entityNumCtrl.setErrors({ serverError: true });
        },
      });
  }
}
